import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LuLogIn, LuSave } from "react-icons/lu";
import useAuth from "../hooks/useAuth";
import { validateEmail } from "../helpers/validateEmail";
import userService from "../services/userService";
import SpinnerOverlay from "../Components/SpinnerOverlay";

const initialForm = {
    name: "",
    lastName: "",
    email: "",
    dni: "",
    phone: "",
};

const Profile = () => {
    const { isAuthenticated, user, updateUserData, logout } = useAuth();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(initialForm);
    const [checkingToken, setCheckingToken] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    useEffect(() => {
        if (!user) return;
        setFormData({
            name: user.name || "",
            lastName: user.lastName || "",
            email: user.email || "",
            dni: user.dni || "",
            phone: user.phone || "",
        });
    }, [user]);

    useEffect(() => {
        if (!isAuthenticated) return;

        let isMounted = true;

        const verifyTokenAndLoadProfile = async () => {
            setCheckingToken(true);
            try {
                const profile = await userService.getMyProfile();
                if (!isMounted) return;

                setFormData({
                    name: profile.name || "",
                    lastName: profile.lastName || "",
                    email: profile.email || "",
                    dni: profile.dni || "",
                    phone: profile.phone || "",
                });
            } catch (err) {
                if (!isMounted) return;
                const status = err?.response?.status;
                if (status === 500 || status === 401 || status === 403) {
                    logout();
                    navigate("/login");
                    return;
                }
                setError("No se pudieron cargar tus datos.");
            } finally {
                if (isMounted) setCheckingToken(false);
            }
        };

        verifyTokenAndLoadProfile();
        return () => {
            isMounted = false;
        };
    }, [isAuthenticated, logout, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const formattedValue =
            name === "dni" || name === "phone" ? value.replace(/\D/g, "") : value;

        setFormData((prevState) => ({
            ...prevState,
            [name]: formattedValue,
        }));
        setError("");
        setSuccess("");
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            return "El nombre es obligatorio.";
        }
        if (!validateEmail(formData.email)) {
            return "El email ingresado no es valido.";
        }
        if (!formData.dni || !/^\d+$/.test(formData.dni) || formData.dni.length < 7) {
            return "El DNI debe contener solo numeros y al menos 7 digitos.";
        }
        if (!formData.phone || !/^\d+$/.test(formData.phone)) {
            return "El telefono debe contener solo numeros.";
        }
        return "";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setSaving(true);
        try {
            const payload = {
                name: formData.name.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.trim().toLowerCase(),
                dni: formData.dni.trim(),
                phone: formData.phone.trim(),
            };

            const updatedUser = await userService.updateMyProfile(payload);
            updateUserData({
                ...user,
                ...updatedUser,
            });
            setSuccess("Tus datos se actualizaron correctamente.");
        } catch (err) {
            const status = err?.response?.status;
            if (status === 500 || status === 401 || status === 403) {
                logout();
                navigate("/login");
                return;
            }
            setError(
                err?.response?.data?.error ||
                    err?.message ||
                    "No se pudieron guardar los cambios."
            );
        } finally {
            setSaving(false);
        }
    };

    if (!isAuthenticated) {
        return (
            <section className="mx-auto mt-10 flex min-h-[58vh] w-full max-w-3xl items-center justify-center px-4 pt-24">
                <div className="w-full rounded-3xl border border-zinc-800 bg-zinc-900/65 p-8 text-center shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_20px_60px_rgba(0,0,0,0.35)] backdrop-blur-sm">
                    <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-zinc-800 text-rose-300">
                        <LuLogIn className="text-2xl" />
                    </div>
                    <h1 className="text-3xl font-bold text-white">
                        Iniciá sesión para ver tus datos
                    </h1>
                    <p className="mx-auto mt-3 max-w-xl text-zinc-300">
                        Entrá a tu cuenta para poder editar tu perfil y mantener tu
                        información actualizada.
                    </p>
                    <div className="mt-7 flex justify-center gap-3">
                        <Link
                            to="/login"
                            className="rounded-xl bg-gradient-to-r from-rose-500 to-rose-600 px-5 py-2.5 font-medium text-white shadow-[0_10px_20px_rgba(244,63,94,0.25)] transition-transform hover:scale-[1.02]"
                        >
                            Iniciar sesión
                        </Link>
                        <Link
                            to="/"
                            className="rounded-xl border border-zinc-700 bg-zinc-900/70 px-5 py-2.5 font-medium text-zinc-100 transition-colors hover:border-zinc-500 hover:bg-zinc-800"
                        >
                            Ver eventos
                        </Link>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <>
            <SpinnerOverlay isLoading={saving || checkingToken} />
            <div className="relative min-h-screen overflow-hidden bg-zinc-950">
                <div
                    className="pointer-events-none absolute inset-0 opacity-70"
                    aria-hidden="true"
                    style={{
                        background:
                            "radial-gradient(circle at 12% 18%, rgba(255, 74, 110, 0.20), transparent 34%), radial-gradient(circle at 85% 15%, rgba(59, 130, 246, 0.14), transparent 28%), radial-gradient(circle at 50% 85%, rgba(250, 204, 21, 0.08), transparent 36%)",
                    }}
                />
                <div className="relative mx-auto max-w-3xl px-4 pb-16 pt-24">
                    <div className="rounded-3xl border border-zinc-800 bg-zinc-900/65 p-8 shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_20px_60px_rgba(0,0,0,0.35)] backdrop-blur-sm">
                        <h1 className="text-3xl font-bold text-white">Mis Datos</h1>
                        <p className="mt-2 text-sm text-zinc-300">
                            Actualizá tu información personal para mantener tu cuenta
                            al día.
                        </p>

                        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                            <div>
                                <label
                                    htmlFor="name"
                                    className="mb-1 block text-sm text-zinc-300"
                                >
                                    Nombre
                                </label>
                                <input
                                    id="name"
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-3 py-2.5 text-white outline-none transition focus:border-rose-500/70"
                                    required
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="lastName"
                                    className="mb-1 block text-sm text-zinc-300"
                                >
                                    Apellido
                                </label>
                                <input
                                    id="lastName"
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-3 py-2.5 text-white outline-none transition focus:border-rose-500/70"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="email"
                                    className="mb-1 block text-sm text-zinc-300"
                                >
                                    Email
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-3 py-2.5 text-white outline-none transition focus:border-rose-500/70"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <label
                                        htmlFor="dni"
                                        className="mb-1 block text-sm text-zinc-300"
                                    >
                                        DNI
                                    </label>
                                    <input
                                        id="dni"
                                        type="text"
                                        name="dni"
                                        value={formData.dni}
                                        onChange={handleChange}
                                        className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-3 py-2.5 text-white outline-none transition focus:border-rose-500/70"
                                        minLength={7}
                                        required
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="phone"
                                        className="mb-1 block text-sm text-zinc-300"
                                    >
                                        Teléfono
                                    </label>
                                    <input
                                        id="phone"
                                        type="text"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-3 py-2.5 text-white outline-none transition focus:border-rose-500/70"
                                        required
                                    />
                                </div>
                            </div>

                            {error && (
                                <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                                    {error}
                                </p>
                            )}

                            {success && (
                                <p className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">
                                    {success}
                                </p>
                            )}

                            <button
                                type="submit"
                                disabled={saving}
                                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-rose-500 to-rose-600 px-5 py-3 font-medium text-white shadow-[0_10px_20px_rgba(244,63,94,0.25)] transition-transform hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <LuSave className="text-lg" />
                                {saving ? "Guardando..." : "Guardar cambios"}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Profile;
